import { FC, useEffect, useState } from 'react';
import { Options, SeriesLineOptions } from 'highcharts';
import Highcharts from 'highcharts/highstock';
import { DeviceConsumptionResult, DeviceReadingsResult } from 'types';
import SimpleLineGraph from 'components/SimpleLineGraph/SimpleLineGraph';
import {
    addBatteryCritSeries,
    addBatteryWarnSeries,
    addButtonPressSeries,
    addCfSeries,
    addConSeries,
    addCovSeries,
    addHddSeries,
    addIdealDeliveryLine,
    addTankSeries,
    addTempSeries,
    addThresholdSeries,
    addTransmissionSeries,
    addVoltageSeries,
} from './helperFunctions/series';
import {
    getLastConsumption,
    getLastPredictionDate,
    getLastReading,
} from './helperFunctions/general';
import { getBaseOptions } from './helperFunctions/baseOptions';
import { addAdminOnlyOptions } from './helperFunctions/adminOnlyOptions';
import { setXAxis } from './helperFunctions/setXAxis';
import DarkTheme from 'highcharts/themes/high-contrast-dark';
import LightTheme from 'highcharts/themes/high-contrast-light';
import { useUi } from 'hooks/useUi';

interface DeviceInformationUsageGraphProps {
    readings: DeviceReadingsResult | null;
    consumption: DeviceConsumptionResult | null;
    admin: boolean;
    tooltipGroupingEnabled: boolean;
}

const addAdminOnlySeries = (
    series: SeriesLineOptions[],
    readings: DeviceReadingsResult
) => {
    addBatteryWarnSeries(series, readings);
    addBatteryCritSeries(series, readings);
    addVoltageSeries(series, readings);
    addCfSeries(series, readings);
};

const DeviceInformationUsageGraph: FC<DeviceInformationUsageGraphProps> = ({
    readings,
    consumption,
    admin,
    tooltipGroupingEnabled,
}) => {
    const [series, setSeries] = useState<SeriesLineOptions[]>([]);
    const [options, setOptions] = useState<Options>(() => getBaseOptions());
    const { ui } = useUi();
    const [showCharts, setShowCharts] = useState(false);

    // Set graph theme based on user's color theme.
    useEffect(() => {
        if (ui.theme === 'dark') DarkTheme(Highcharts);
        else LightTheme(Highcharts);
    }, [ui.theme]);

    useEffect(() => {
        const tempSeries: SeriesLineOptions[] = [];
        const tempOptions: Options = getBaseOptions();
        if (admin) addAdminOnlyOptions(tempOptions);

        if (readings) {
            const lastReading = getLastReading(readings);
            addTransmissionSeries(tempSeries, readings);
            addThresholdSeries(tempSeries, readings);
            addButtonPressSeries(tempSeries, readings);
            addCovSeries(tempSeries, readings);

            if (consumption) {
                const lastConsumption = getLastConsumption(consumption);
                const lastPredictionDate = getLastPredictionDate(
                    lastReading,
                    lastConsumption,
                    consumption
                );
                addTankSeries(
                    tempSeries,
                    lastConsumption,
                    readings,
                    consumption
                );
                setXAxis(tempOptions, lastPredictionDate);
                addConSeries(tempSeries, lastReading, consumption);
                addHddSeries(tempSeries, lastReading, consumption);
                addTempSeries(tempSeries, consumption);
            }

            if (admin) addAdminOnlySeries(tempSeries, readings);
        } else if (consumption) {
            addTempSeries(tempSeries, consumption);
        }

        addIdealDeliveryLine(tempSeries);
        tempOptions.series = tempSeries;
        if (!!tempOptions.tooltip)
            tempOptions.tooltip.split = tooltipGroupingEnabled;
        setSeries(tempSeries);
        let animationComplete = false;

        setOptions({
            chart: {
                animation: {
                    complete: () => {
                        if (!animationComplete) {
                            animationComplete = true;
                            setOptions({
                                ...tempOptions,
                                chart: { height: '60%' },
                            });
                        }
                    },
                },
            },
            ...tempOptions,
        });
        setShowCharts(true);
    }, [admin, consumption, readings, tooltipGroupingEnabled]);

    return (
        <div data-testid="DeviceInformationUsageGraph">
            {showCharts && (
                <SimpleLineGraph
                    options={options}
                    series={series}
                    highcharts={Highcharts}
                />
            )}
        </div>
    );
};

export default DeviceInformationUsageGraph;
